import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import Close from "@mui/icons-material/Close";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Download from "@mui/icons-material/Download";
import AccountTree from "@mui/icons-material/AccountTree";
import PersonOutline from "@mui/icons-material/PersonOutline";
import History from "@mui/icons-material/History";
import {
  actorLabel,
  displayValue,
  downloadFile,
  formatTime,
  getChangeRows,
  humanize,
  isRecord,
  localTimezone,
} from "./auditLogUtils";

export function StatusChip({ status }) {
  const config = {
    SUCCESS: { color: "success", icon: <CheckCircleOutline /> },
    FAILURE: { color: "error", icon: <ErrorOutline /> },
    WARNING: { color: "warning", icon: <WarningAmber /> },
  }[status] || { color: "default" };
  return (
    <Chip
      size="small"
      variant="outlined"
      label={humanize(status)}
      {...config}
      sx={{ fontWeight: 600, fontSize: 12 }}
    />
  );
}

function JsonNode({ name, value, depth = 0 }) {
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value);
    return (
      <Box
        component="details"
        open={depth === 0}
        sx={{ pl: depth ? 1.5 : 0, py: 0.35 }}
      >
        <Box
          component="summary"
          sx={{
            cursor: "pointer",
            color: "primary.main",
            overflowWrap: "anywhere",
          }}
        >
          {name}{" "}
          {Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`}
        </Box>
        {entries.map(([key, child]) => (
          <JsonNode key={key} name={key} value={child} depth={depth + 1} />
        ))}
      </Box>
    );
  }
  return (
    <Box
      sx={{
        pl: 1.5,
        py: 0.35,
        overflowWrap: "anywhere",
        whiteSpace: "pre-wrap",
      }}
    >
      <Box component="span" sx={{ color: "text.secondary" }}>
        {name}:{" "}
      </Box>
      {JSON.stringify(value)}
    </Box>
  );
}

function Field({ label, value, copy }) {
  const present = value !== null && value !== undefined && value !== "";
  return (
    <Box sx={{ py: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", flex: 1 }}
        >
          {present ? displayValue(value) : "Not recorded"}
        </Typography>
        {copy && present && (
          <Tooltip title={`Copy ${label}`}>
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              onClick={() => copy(String(value))}
            >
              <ContentCopy sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

export default function LogDetailsDrawer({ log, onClose, onTrack, notify }) {
  const [tab, setTab] = useState("overview");
  const details = isRecord(log.details) ? log.details : {};
  const request = isRecord(details.request) ? details.request : {};
  const target = isRecord(details.target) ? details.target : {};
  const changes = getChangeRows(details.changes);
  const requestId = log.request_id || request.request_id;
  const targetId = log.target_id || target.id;
  const targetType = log.target_type || target.type;
  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      notify("Copied to clipboard.");
    } catch {
      notify(
        "Unable to copy. You can select and copy the text manually.",
        "error",
      );
    }
  };
  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "audit-detail-title",
        sx: { width: { xs: "100%", sm: 640 }, maxWidth: "100%" },
      }}
    >
      <Box sx={{ p: 3, pb: 2 }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={2}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              Audit event · {humanize(log.category)}
            </Typography>
            <Typography
              id="audit-detail-title"
              variant="h6"
              fontWeight={700}
              sx={{ overflowWrap: "anywhere" }}
            >
              {humanize(log.action)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflowWrap: "anywhere" }}
            >
              {log.action}
            </Typography>
          </Box>
          <IconButton aria-label="Close log details" onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 2 }}>
          <StatusChip status={log.status} />
          <Typography variant="caption" color="text.secondary">
            {formatTime(log.created_at)} · {localTimezone}
          </Typography>
        </Stack>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        aria-label="Log detail sections"
        sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          id="audit-tab-overview"
          aria-controls="audit-panel-overview"
          value="overview"
          label="Overview"
        />
        {details.changes != null && (
          <Tab
            id="audit-tab-changes"
            aria-controls="audit-panel-changes"
            value="changes"
            label={`Changes${changes.length ? ` (${changes.length})` : ""}`}
          />
        )}
        <Tab
          id="audit-tab-json"
          aria-controls="audit-panel-json"
          value="json"
          label="Raw JSON"
        />
      </Tabs>
      <Box
        role="tabpanel"
        id={`audit-panel-${tab}`}
        aria-labelledby={`audit-tab-${tab}`}
        sx={{ p: 3, flex: 1, overflowY: "auto" }}
      >
        {tab === "overview" && (
          <Stack spacing={2}>
            {details.error && (
              <Alert
                severity={
                  log.status === "FAILURE"
                    ? "error"
                    : log.status === "WARNING"
                      ? "warning"
                      : "info"
                }
                sx={{ "& .MuiAlert-message": { minWidth: 0, width: "100%" } }}
              >
                <Typography variant="subtitle2">
                  {log.status === "FAILURE"
                    ? "Recorded error"
                    : "Recorded message"}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    fontSize: 12,
                  }}
                >
                  {displayValue(details.error)}
                </Box>
              </Alert>
            )}
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Who & what
              </Typography>
              <Field label="Actor" value={actorLabel(log)} />
              <Field
                label="Actor email"
                value={log.actor_email || log.actor?.email}
                copy={copy}
              />
              <Field
                label="Actor ID"
                value={log.actor_id || log.actor?.u_id}
                copy={copy}
              />
              <Field label="Target" value={target.label || targetType} />
              <Field label="Target ID" value={targetId} copy={copy} />
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                Request & source
              </Typography>
              <Field label="Request ID" value={requestId} copy={copy} />
              <Field
                label="IP address"
                value={log.ip_address || request.ip_address}
                copy={copy}
              />
              <Field
                label="User agent"
                value={log.user_agent || request.user_agent}
              />
              {(request.method || request.path) && (
                <Field
                  label="Endpoint"
                  value={[request.method, request.path]
                    .filter(Boolean)
                    .join(" ")}
                />
              )}
              <Field label="Source" value={details.source} />
              <Field label="Trigger" value={details.trigger} />
              <Field label="Event ID" value={log.id} copy={copy} />
            </Box>
            {details.external && (
              <Box>
                <Typography variant="subtitle2">External service</Typography>
                <JsonNode name="external" value={details.external} />
              </Box>
            )}
            {details.meta && (
              <Box>
                <Typography variant="subtitle2">Additional context</Typography>
                <Box sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  <JsonNode name="meta" value={details.meta} depth={1} />
                </Box>
              </Box>
            )}
          </Stack>
        )}
        {tab === "changes" && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Values as recorded by this event. “Not recorded” means the log did
              not include that value.
            </Typography>
            {changes.length ? (
              <Table
                size="small"
                sx={{
                  tableLayout: "fixed",
                  "& td": {
                    verticalAlign: "top",
                    overflowWrap: "anywhere",
                    whiteSpace: "pre-wrap",
                    fontSize: 12,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Before</TableCell>
                    <TableCell>After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changes.map((change) => (
                    <TableRow key={change.field}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {change.field}
                      </TableCell>
                      <TableCell sx={{ bgcolor: "action.hover" }}>
                        {displayValue(change.before)}
                      </TableCell>
                      <TableCell sx={{ color: "primary.main" }}>
                        {displayValue(change.after)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <JsonNode name="changes" value={details.changes} />
            )}
          </>
        )}
        {tab === "json" && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={() => copy(JSON.stringify(log, null, 2))}
              >
                Copy JSON
              </Button>
              <Button
                size="small"
                startIcon={<Download />}
                onClick={() =>
                  downloadFile(
                    `audit-event-${String(log.id || "details").replace(/[^a-zA-Z0-9_-]/g, "_")}.json`,
                    JSON.stringify(log, null, 2),
                    "application/json",
                  )
                }
              >
                Download JSON
              </Button>
            </Stack>
            <Box
              sx={{
                p: 2,
                bgcolor: "action.hover",
                borderRadius: 2,
                fontFamily: "monospace",
                fontSize: 12,
              }}
            >
              <JsonNode name="event" value={log} />
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Typography variant="caption" color="text.secondary">
          Explore related events across all time
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTree />}
            disabled={!requestId}
            onClick={() => onTrack({ request_id: requestId })}
          >
            Same request
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonOutline />}
            disabled={!(log.actor_id || log.actor?.u_id)}
            onClick={() =>
              onTrack({ actor_id: log.actor_id || log.actor?.u_id })
            }
          >
            Same user
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<History />}
            disabled={!targetId}
            onClick={() =>
              onTrack({ target_id: targetId, target_type: targetType || "" })
            }
          >
            Target history
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
